import { DOCUMENT, Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export const LANGUAGES = [
  'en', 'ar', 'de', 'es', 'gu', 'hi', 'hy', 'id', 'ja', 'km', 'ko', 'my', 'te', 'th', 'tl', 'vi', 'zh-CN', 'zh-TW',
] as const;

export const LANGUAGE_COOKIE_NAME = 'lang';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly document = inject(DOCUMENT);
  private initialized = false;
  private messageSource = new BehaviorSubject('en');
  currentMessage = this.messageSource.asObservable();

  initialize(): string {
    if (this.initialized) return this.getActiveLanguage();
    this.initialized = true;
    const cookieLanguage = this.readCookie();
    const language = this.isSupported(cookieLanguage) ? cookieLanguage : 'en';
    this.changeMessage(language);
    return language;
  }

  changeMessage(language: string): void {
    const selectedLanguage = this.isSupported(language) ? language : 'en';
    this.writeCookie(selectedLanguage);
    this.messageSource.next(selectedLanguage);
  }

  getActiveLanguage(): string {
    return this.messageSource.value;
  }

  private isSupported(language: string | null): language is (typeof LANGUAGES)[number] {
    return language !== null && (LANGUAGES as readonly string[]).includes(language);
  }

  private readCookie(): string | null {
    const prefix = `${LANGUAGE_COOKIE_NAME}=`;
    const cookie = this.document.cookie.split('; ').find((entry) => entry.startsWith(prefix));
    return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
  }

  private writeCookie(language: string): void {
    const hostname = this.document.defaultView?.location.hostname ?? '';
    const secure = this.document.defaultView?.location.protocol === 'https:' ? '; Secure' : '';
    const isAssessorHost = /^assessor[a-z0-9-]*\.lacounty\.gov$/i.test(hostname);
    const domain = isAssessorHost ? '; Domain=.lacounty.gov' : '';
    this.document.cookie = `${LANGUAGE_COOKIE_NAME}=${encodeURIComponent(language)}; Path=/; Max-Age=31536000; SameSite=Lax${secure}${domain}`;
  }
}
